import { useState, type FormEvent } from 'react'
import { salesConfig } from './config'
import { useLicense } from './licensing'

type LicenseController = ReturnType<typeof useLicense>

const fallbackHref = 'mailto:catalogfix.team@gmail.com?subject=CatalogFix%20founding%20access'

function CheckoutLink({ href, children, featured = false }: { href: string; children: string; featured?: boolean }) {
  const checkoutReady = salesConfig.checkoutEnabled && Boolean(href)
  return <a
    className={`button ${featured ? 'primary' : 'secondary'} pricing-button`}
    href={checkoutReady ? href : fallbackHref}
    target={checkoutReady ? '_blank' : undefined}
    rel={checkoutReady ? 'noreferrer' : undefined}
  >{checkoutReady ? children : 'Request founding access'} <span aria-hidden="true">↗</span></a>
}

export function PricingSection() {
  return <section className="pricing-section" id="pricing">
    <div className="section-heading pricing-heading">
      <div><p className="eyebrow"><span aria-hidden="true" /> Simple pricing</p><h2>Catch one bad import.<br /><em>It already paid for itself.</em></h2></div>
      <p>Start with a 14-day Pro trial. Catalog files stay in your browser; billing and license delivery are handled securely by Lemon Squeezy.</p>
    </div>
    <div className="pricing-grid">
      <article className="price-card free-card">
        <p className="plan-label">Free preview</p>
        <div className="price"><strong>€0</strong><span>no card</span></div>
        <p>Inspect a supplier file and see what CatalogFix catches before committing.</p>
        <ul><li>Up to {salesConfig.freeRowLimit} rows</li><li>Column mapping</li><li>Issue report</li><li>Local browser processing</li></ul>
        <a className="button secondary pricing-button" href="/demo">Open WooCommerce CSV Prep <span aria-hidden="true">↗</span></a>
      </article>
      <article className="price-card featured">
        <span className="value-badge">Save €29 / year</span>
        <p className="plan-label">Pro annual</p>
        <div className="price"><strong>€79</strong><span>per year</span></div>
        <p>Best value for stores that receive recurring supplier price and stock files.</p>
        <ul><li>14-day free trial</li><li>Unlimited catalog rows</li><li>WooCommerce CSV export</li><li>2 browser activations</li></ul>
        <CheckoutLink href={salesConfig.annualCheckoutUrl} featured>Start annual trial</CheckoutLink>
      </article>
      <article className="price-card">
        <p className="plan-label">Pro monthly</p>
        <div className="price"><strong>€9</strong><span>per month</span></div>
        <p>Flexible access for occasional migrations, launches and catalog repair work.</p>
        <ul><li>14-day free trial</li><li>Unlimited catalog rows</li><li>WooCommerce CSV export</li><li>2 browser activations</li></ul>
        <CheckoutLink href={salesConfig.monthlyCheckoutUrl}>Start monthly trial</CheckoutLink>
      </article>
    </div>
    {!salesConfig.checkoutEnabled && <p className="checkout-note">Live checkout opens after store approval. Founding access requests go directly to the CatalogFix team.</p>}
  </section>
}

export function LicenseSection({ license }: { license: LicenseController }) {
  const [email, setEmail] = useState('')
  const [licenseKey, setLicenseKey] = useState('')

  const onSubmit = async (event: FormEvent) => {
    event.preventDefault()
    await license.activate(licenseKey, email)
  }

  return <section className="license-section" id="license">
    <div className="license-copy">
      <p className="eyebrow"><span aria-hidden="true" /> Pro access</p>
      <h2>Already subscribed?</h2>
      <p>Use the checkout email and license key from your Lemon Squeezy receipt. The key is verified without sending your catalog file.</p>
      <p className="license-privacy">Stored only in this browser. You can deactivate this browser at any time.</p>
    </div>
    {license.isActive ? (
      <div className="license-card license-active">
        <span className="license-status-dot" aria-hidden="true" />
        <div><strong>CatalogFix Pro is active</strong><p>{license.message}</p></div>
        <button className="button secondary" type="button" onClick={license.deactivate}>Deactivate browser</button>
      </div>
    ) : (
      <form className="license-card" onSubmit={onSubmit}>
        <label className="field"><span>Checkout email</span><input type="email" autoComplete="email" required value={email} onChange={(event) => setEmail(event.target.value)} placeholder="you@company.com" /></label>
        <label className="field"><span>License key</span><input type="text" autoComplete="off" required value={licenseKey} onChange={(event) => setLicenseKey(event.target.value)} placeholder="xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx" /></label>
        {license.message && <p className={`license-message ${license.status}`} role="status">{license.message}</p>}
        <button className="button primary" type="submit" disabled={license.status === 'checking'}>{license.status === 'checking' ? 'Checking…' : 'Activate Pro'}</button>
      </form>
    )}
  </section>
}
